/**
 * Bill Admin Page
 *
 * Hidden SuperAdmin interface for managing Companies, Brands, and POS Config.
 * Accessible at /bill-admin
 *
 * @see Sprint 20 - Interface Super Admin /bill-admin
 */

import { useState } from 'react';
import {
  Building2,
  ChevronDown,
  ChevronRight,
  FolderTree,
  MapPin,
  Warehouse,
  Pencil,
  Plus,
  AlertCircle,
} from 'lucide-react';
import { PageHeader, Button, Badge, Loader, EmptyState } from '@/components/ui';
import { useAdminCompanies, useAdminCompany } from '../hooks/useAdminCompanies';
import { useCreateCompany, useUpdateCompany } from '../hooks/useAdminMutations';
import { CompanyDrawer } from '../components/CompanyDrawer';
import { BrandDrawer } from '../components/BrandDrawer';
import type {
  AdminCompanyListItem,
  AdminCompanyDetail,
  AdminBrandTreeItem,
  CreateAdminCompanyInput,
  UpdateAdminCompanyInput,
} from '@bstock/shared';

export function BillAdminPage() {
  // Expanded companies state
  const [expandedCompanies, setExpandedCompanies] = useState<Set<string>>(new Set());

  // Drawer states
  const [companyDrawerOpen, setCompanyDrawerOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<AdminCompanyDetail | null>(null);
  const [isCreatingCompany, setIsCreatingCompany] = useState(false);

  const [brandDrawerOpen, setBrandDrawerOpen] = useState(false);
  const [selectedBrandId, setSelectedBrandId] = useState<string | null>(null);
  const [selectedCompanyForBrand, setSelectedCompanyForBrand] = useState<string>('');
  const [availableGroupsForBrand, setAvailableGroupsForBrand] = useState<AdminBrandTreeItem[]>([]);

  // Queries
  const { data: companiesData, isLoading, error, refetch } = useAdminCompanies();
  const companies = companiesData?.companies ?? [];

  // Mutations
  const createCompanyMutation = useCreateCompany();
  const updateCompanyMutation = useUpdateCompany();

  // Toggle company expansion
  const toggleCompany = (companyId: string) => {
    const newExpanded = new Set(expandedCompanies);
    if (newExpanded.has(companyId)) {
      newExpanded.delete(companyId);
    } else {
      newExpanded.add(companyId);
    }
    setExpandedCompanies(newExpanded);
  };

  // Open company drawer for creation
  const handleNewCompany = () => {
    setSelectedCompany(null);
    setIsCreatingCompany(true);
    setCompanyDrawerOpen(true);
  };

  // Open company drawer for editing (called from CompanyCard with full detail)
  const handleEditCompany = (company: AdminCompanyDetail) => {
    setIsCreatingCompany(false);
    setSelectedCompany(company);
    setCompanyDrawerOpen(true);
  };

  // Save company
  const handleSaveCompany = async (data: CreateAdminCompanyInput | UpdateAdminCompanyInput) => {
    if (isCreatingCompany) {
      await createCompanyMutation.mutateAsync(data as CreateAdminCompanyInput);
    } else if (selectedCompany) {
      await updateCompanyMutation.mutateAsync({
        id: selectedCompany.id,
        data: data as UpdateAdminCompanyInput,
      });
    }
    await refetch();
  };

  // Open brand drawer for creation
  const handleNewBrand = (companyId: string, groups: AdminBrandTreeItem[]) => {
    setSelectedBrandId(null);
    setSelectedCompanyForBrand(companyId);
    setAvailableGroupsForBrand(groups);
    setBrandDrawerOpen(true);
  };

  // Open brand drawer for editing
  const handleEditBrand = (brandId: string, companyId: string, groups: AdminBrandTreeItem[]) => {
    setSelectedBrandId(brandId);
    setSelectedCompanyForBrand(companyId);
    setAvailableGroupsForBrand(groups);
    setBrandDrawerOpen(true);
  };

  // Brand saved successfully
  const handleBrandSuccess = async () => {
    await refetch();
  };

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <EmptyState
          icon={AlertCircle}
          title="Erreur de chargement"
          description="Impossible de charger les entreprises"
          action={{
            label: 'Reessayer',
            onClick: () => refetch(),
          }}
        />
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col p-6 bg-neutral-50 min-h-screen">
      <PageHeader
        title="Bill Admin"
        subtitle="Gestion des entreprises, brands et configurations POS"
        action={{
          label: 'Nouvelle entreprise',
          onClick: handleNewCompany,
          icon: <Plus className="w-4 h-4" />,
        }}
      />

      <div className="mt-6 space-y-4">
        {companies.length === 0 ? (
          <EmptyState
            icon={Building2}
            title="Aucune entreprise"
            description="Commencez par creer une entreprise"
            action={{
              label: 'Nouvelle entreprise',
              onClick: handleNewCompany,
              icon: Plus,
            }}
          />
        ) : (
          companies.map((company) => (
            <CompanyCard
              key={company.id}
              company={company}
              isExpanded={expandedCompanies.has(company.id)}
              onToggle={() => toggleCompany(company.id)}
              onEdit={(detail) => handleEditCompany(detail)}
              onNewBrand={(groups) => handleNewBrand(company.id, groups)}
              onEditBrand={(brandId, groups) => handleEditBrand(brandId, company.id, groups)}
            />
          ))
        )}
      </div>

      {/* Company Drawer */}
      <CompanyDrawer
        company={selectedCompany}
        open={companyDrawerOpen}
        onOpenChange={setCompanyDrawerOpen}
        onSave={handleSaveCompany}
        isLoading={createCompanyMutation.isPending || updateCompanyMutation.isPending}
      />

      {/* Brand Drawer */}
      <BrandDrawer
        brandId={selectedBrandId}
        companyId={selectedCompanyForBrand}
        availableGroups={availableGroupsForBrand}
        open={brandDrawerOpen}
        onOpenChange={setBrandDrawerOpen}
        onSuccess={handleBrandSuccess}
      />
    </div>
  );
}

// =============================================================================
// COMPANY CARD COMPONENT
// =============================================================================

interface CompanyCardProps {
  company: AdminCompanyListItem;
  isExpanded: boolean;
  onToggle: () => void;
  onEdit: (detail: AdminCompanyDetail) => void;
  onNewBrand: (groups: AdminBrandTreeItem[]) => void;
  onEditBrand: (brandId: string, groups: AdminBrandTreeItem[]) => void;
}

function CompanyCard({
  company,
  isExpanded,
  onToggle,
  onEdit,
  onNewBrand,
  onEditBrand,
}: CompanyCardProps) {
  // Fetch full company detail when expanded or when clicking edit
  const { data: companyDetail, isLoading } = useAdminCompany(company.id);

  // Get all GROUP brands for parent selection
  const getGroupBrands = (brands: AdminBrandTreeItem[]): AdminBrandTreeItem[] => {
    return brands.filter((b) => b.type === 'GROUP');
  };

  const groups = companyDetail ? getGroupBrands(companyDetail.brands) : [];

  return (
    <div className="bg-white border border-neutral-200 rounded-xl shadow-sm overflow-hidden">
      {/* Company Header */}
      <div
        className="flex items-center gap-3 p-4 cursor-pointer hover:bg-neutral-50 transition-colors"
        onClick={onToggle}
      >
        <button className="p-1 hover:bg-neutral-100 rounded">
          {isExpanded ? (
            <ChevronDown className="w-5 h-5 text-neutral-400" />
          ) : (
            <ChevronRight className="w-5 h-5 text-neutral-400" />
          )}
        </button>

        <Building2 className="w-6 h-6 text-primary-500" />

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-dark-500 truncate">{company.name}</h3>
            {!company.isActive && <Badge variant="neutral">Inactif</Badge>}
          </div>
          <div className="flex items-center gap-3 text-sm text-neutral-500 mt-0.5">
            <span>{company.currency}</span>
            <span>{company.brandsCount} brands</span>
            <span>{company.usersCount} users</span>
          </div>
        </div>

        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            if (companyDetail) onEdit(companyDetail);
          }}
          disabled={!companyDetail}
        >
          <Pencil className="w-4 h-4 mr-1" />
          Modifier
        </Button>
      </div>

      {/* Expanded Content - Brands Tree */}
      {isExpanded && (
        <div className="border-t border-neutral-200 p-4 pl-14 bg-neutral-50">
          {isLoading ? (
            <div className="flex items-center gap-2 py-4">
              <Loader size="sm" />
              <span className="text-sm text-neutral-500">Chargement des brands...</span>
            </div>
          ) : companyDetail && companyDetail.brands.length > 0 ? (
            <div className="space-y-2">
              {companyDetail.brands.map((brand) => (
                <BrandTreeNode
                  key={brand.id}
                  brand={brand}
                  level={0}
                  groups={groups}
                  onEdit={(brandId) => onEditBrand(brandId, groups)}
                />
              ))}
            </div>
          ) : (
            <div className="text-sm text-neutral-500 py-2">Aucun brand</div>
          )}

          {/* Add brand button */}
          <div className="mt-4 pt-4 border-t border-neutral-200">
            <Button variant="secondary" size="sm" onClick={() => onNewBrand(groups)}>
              <Plus className="w-4 h-4 mr-1" />
              Nouveau brand
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// BRAND TREE NODE COMPONENT
// =============================================================================

interface BrandTreeNodeProps {
  brand: AdminBrandTreeItem;
  level: number;
  groups: AdminBrandTreeItem[];
  onEdit: (brandId: string) => void;
}

function BrandTreeNode({ brand, level, groups, onEdit }: BrandTreeNodeProps) {
  const Icon = brand.type === 'GROUP' ? FolderTree : brand.type === 'CENTRAL' ? Warehouse : MapPin;
  const iconColor = brand.type === 'GROUP' ? 'text-info-500' : brand.type === 'CENTRAL' ? 'text-warning-500' : 'text-success-500';

  return (
    <div>
      <div
        className="flex items-center gap-2 py-2 px-3 rounded-lg hover:bg-white transition-colors group"
        style={{ marginLeft: `${level * 24}px` }}
      >
        <Icon className={`w-4 h-4 ${iconColor}`} />

        <span className="flex-1 text-sm font-medium text-dark-500">{brand.name}</span>

        <div className="flex items-center gap-2">
          <Badge variant={brand.type === 'GROUP' ? 'info' : brand.type === 'CENTRAL' ? 'warning' : 'success'}>
            {brand.type}
          </Badge>

          {brand.type === 'GROUP' && brand.hasPosConfig && <Badge variant="primary">POS</Badge>}

          {!brand.isActive && <Badge variant="neutral">Inactif</Badge>}

          <Button
            variant="ghost"
            size="sm"
            onClick={() => onEdit(brand.id)}
            className="opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <Pencil className="w-3 h-3 mr-1" />
            Modifier
          </Button>
        </div>
      </div>

      {/* Render children recursively */}
      {brand.children.length > 0 && (
        <div>
          {brand.children.map((child) => (
            <BrandTreeNode
              key={child.id}
              brand={child}
              level={level + 1}
              groups={groups}
              onEdit={onEdit}
            />
          ))}
        </div>
      )}
    </div>
  );
}
